// Multi-org access service - the switcher and the super-admin mapping control.
// Uses the shared `api` instance so the auth header, refresh flow and the
// global 403 "Access Denied" notification all behave the same as everywhere else.
import { notification } from 'antd';
import api from './api';
import { dedupedGet } from '../utils/api/dedupedFetch';
import config from '../config';
import { STORAGE_KEYS } from '../utils/permissions';

const normalizeError = (error, fallback) => ({
  success: false,
  data: null,
  message: error?.response?.data?.message || fallback,
  status: error?.response?.status || null,
});

/**
 * Ends the session after an organization switch that the server accepted but
 * this device could not cache. The two sides cannot be reconciled: the session
 * is now in the new org while the cached payload still describes the old one,
 * so continuing would paint the previous organization's branding and
 * permissions over another org's data. A fresh login rebuilds the cache from
 * scratch. Everything here is defensive, since storage is already misbehaving,
 * and the navigation is hard so no stale Redux state survives.
 */
export const forceLogoutAfterFailedSwitch = () => {
  let token = null;
  try {
    token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (e) {
    token = null;
  }

  if (token) {
    // Revoke server-side too, mirroring logoutApi. Failure is ignored on
    // purpose: the local session is ending either way.
    fetch(`${config.api.base_url}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: '{}',
      keepalive: true,
    }).catch(() => {});
  }

  [
    STORAGE_KEYS.ACCESS_TOKEN,
    STORAGE_KEYS.REFRESH_TOKEN,
    STORAGE_KEYS.USER_DATA,
    STORAGE_KEYS.PERMISSIONS,
    'mfaLastConsumedToken',
    'mfaSessionToken',
    'mfaMaskedEmail',
    'mfaExpiryMinutes',
  ].forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Nothing else to try; the redirect below still ends the session.
    }
  });

  notification.error({
    message: 'Signed out',
    description: 'Your organization was switched but could not be saved on this device. Please sign in again.',
    duration: 6,
  });

  window.location.href = '/login';
};

/**
 * Persists the switch response the FE depends on for org branding and
 * permissions. Returns false when the write failed and the caller must stop.
 */
export const persistSwitchedSession = (data) => {
  try {
    if (data?.user) {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data.user));
    }
    if (data?.permissions) {
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(data.permissions));
    }
    return true;
  } catch (e) {
    forceLogoutAfterFailedSwitch();
    return false;
  }
};

/**
 * Organizations the logged-in user can switch between.
 * Returns { multi_org_enabled, active_organization_id, organizations: [{_id, name}] }.
 */
export const getMyOrganizations = async () => {
  try {
    // Deduped: the sidebar and the mobile header both mount a switcher, and
    // only one of them is visible at a time.
    const response = await dedupedGet('myOrganizations', () =>
      api.get('/users/my-organizations')
    );
    return {
      success: !!response.data?.status,
      data: response.data?.data || null,
      message: response.data?.message || '',
      status: response.status,
    };
  } catch (error) {
    return normalizeError(error, 'Failed to load your organizations');
  }
};

/**
 * Cross-org summary plus a paginated slice of studies and projects for the
 * all-organizations dashboard. Params: page, limit, type ('all' | 'campaigns' |
 * 'projects'), organization_id, search, sort ('recent' | 'name'). The org list
 * is always resolved server-side from the caller's mapping, so nothing about it
 * is sent from here. A 403 means the feature is off for this account and the
 * page renders that inline, so the global toast is suppressed for this call.
 */
export const getMyOrganizationsOverview = async (params = {}) => {
  try {
    const response = await api.get('/users/my-organizations/overview', {
      params,
      suppressForbiddenNotification: true,
    });
    return {
      success: !!response.data?.status,
      data: response.data?.data || null,
      message: response.data?.message || '',
      status: response.status,
    };
  } catch (error) {
    return normalizeError(error, 'Failed to load your organizations overview');
  }
};

/**
 * Switches the caller's active organization.
 * Response data carries { active_organization, previous_organization_id, user,
 * permissions, redirect_chat_id } - the caller must persist `user` and
 * `permissions` before reloading or the previous org's branding survives.
 */
export const switchOrganization = async (organizationId) => {
  try {
    const response = await api.post('/users/switch-organization', {
      organization_id: organizationId,
    });
    return {
      success: !!response.data?.status,
      data: response.data?.data || null,
      message: response.data?.message || '',
      status: response.status,
    };
  } catch (error) {
    return normalizeError(error, 'Failed to switch organization');
  }
};

/**
 * Super-admin only: enable/disable multi-org access and set the org mapping
 * for an organization-admin. The server always force-includes the user's
 * current active organization in the saved mapping.
 */
export const setMultiOrgMapping = async (userId, { multi_org_enabled, mapped_organizations }) => {
  try {
    const response = await api.post(`/users/${userId}/multi-org`, {
      multi_org_enabled,
      mapped_organizations,
    });
    return {
      success: !!response.data?.status,
      data: response.data?.data || null,
      message: response.data?.message || '',
      status: response.status,
    };
  } catch (error) {
    return normalizeError(error, 'Failed to save multi-organization access');
  }
};

const multiOrgService = {
  getMyOrganizations,
  switchOrganization,
  setMultiOrgMapping,
};

export default multiOrgService;
